// src/app/layout/components/header/header.component.ts
import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { LayoutService } from '../../services/layout.service';
import { AuthService } from '../../../auth/services/auth.service';
import { ThemeSwitcherComponent } from '../../../components/theme-switcher/theme-switcher.component';
import { BreadcrumbService } from '../../../services/breadcrumb.service';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [CommonModule, ThemeSwitcherComponent, RouterLink],
  template: `
    <header class="h-16 bg-white border-b border-gray-300 enterprise-shadow flex items-center justify-between px-6">
      <div class="flex items-center gap-3">
        <!-- Breadcrumbs -->
        @if (breadcrumbService.breadcrumbs().length > 0) {
          <nav class="flex items-center gap-2 text-sm">
            @for (crumb of breadcrumbService.breadcrumbs(); track crumb.label; let last = $last) {
              @if (crumb.route && !last) {
                <a [routerLink]="crumb.route" class="text-slate-500 hover:text-slate-700 transition-colors">
                  {{ crumb.label }}
                </a>
                <span class="text-slate-300">/</span>
              } @else {
                <span class="text-slate-900 font-semibold">{{ crumb.label }}</span>
              }
            }
          </nav>
        } @else {
          <h1 class="text-xl font-semibold text-slate-900 font-open-sans">{{ breadcrumbService.headerTitle() }}</h1>
        }
      </div>
      
      <div class="flex items-center gap-4">
        <!-- Theme Switcher (Dropdown) -->
        <!-- <app-theme-switcher></app-theme-switcher> -->
        
        <!-- Search -->
        <div class="relative">
          <!--
          <svg xmlns="http://www.w3.org/2000/svg" class="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input 
            type="text" 
            placeholder="Search..." 
            class="pl-9 pr-4 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-80">
          -->
        </div>
        
        <!-- Notifications -->
        <!--
        <button class="relative p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
          </svg>
          <span class="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
        </button>
        -->
        <!-- User menu -->
        <div class="relative" (focusout)="closeDropdown($event)">
          <button class="flex items-center gap-2 p-1.5 rounded-lg hover:bg-slate-100 transition-colors" (click)="toggleDropdown()" aria-haspopup="true" aria-expanded="dropdownOpen">
            <div class="w-8 h-8 bg-gradient-to-br from-violet-600 to-purple-600 rounded-full flex items-center justify-center text-white shadow-sm ring-2 ring-white">
              <span class="text-xs font-bold tracking-wide">{{ layoutService.currentUser().initials }}</span>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          <div *ngIf="dropdownOpen" class="absolute right-0 mt-2 w-56 bg-white border border-gray-300 rounded-lg shadow-lg z-50">
            <div class="px-4 py-3 border-b border-gray-200">
              <p class="text-sm font-medium text-slate-900">{{ layoutService.currentUser().name }}</p>
              <p class="text-xs text-slate-500 truncate">{{ layoutService.currentUser().email }}</p>
            </div>
            <a routerLink="/account" (click)="dropdownOpen = false" class="block w-full text-left px-4 py-2 text-slate-700 hover:bg-slate-100">Account</a>
            <button (click)="logout()" class="w-full text-left px-4 py-2 text-slate-700 hover:bg-slate-100 rounded-b-lg">Logout</button>
          </div>
        </div>
      </div>
    </header>
  `,
  styles: [`
    .enterprise-shadow { box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06); }
  `]
})
export class HeaderComponent {
  layoutService = inject(LayoutService);
  authService = inject(AuthService);
  breadcrumbService = inject(BreadcrumbService);
  dropdownOpen = false;

  toggleDropdown() {
    this.dropdownOpen = !this.dropdownOpen;
  }

  closeDropdown(event: FocusEvent) {
    // Close dropdown if focus leaves the menu
    const relatedTarget = event.relatedTarget as HTMLElement | null;
    if (!relatedTarget || !relatedTarget.closest('.relative')) {
      this.dropdownOpen = false;
    }
  }

  logout() {
    this.authService.logout().subscribe(() => {
      // Navigation is handled by AuthService.logout()
    });
    this.dropdownOpen = false;
  }
}
